import { useTranslation } from "react-i18next";
import {
  AlertTriangle,
  Download,
  FileSpreadsheet,
  FileText,
} from "lucide-react";

import { cn } from "@/utils/cn";
import { formatBytes, isHttpUrl } from "@/utils/file.utils";
import type { IAiGeneratedAttachment } from "@/types/(my-space)/ai-assistant.interface";

interface GeneratedAttachmentsProps {
  attachments?: IAiGeneratedAttachment[];
  errors?: string[];
  className?: string;
}

const FORMAT_STYLES: Record<string, { icon: typeof FileText; tile: string }> = {
  csv: { icon: FileSpreadsheet, tile: "bg-success-muted text-success" },
  pdf: { icon: FileText, tile: "bg-danger-muted text-danger" },
};
const FALLBACK_STYLE = {
  icon: FileText,
  tile: "bg-muted text-muted-foreground",
};

// Download cards for files the AI rendered + uploaded for a turn, plus any
// preparation/upload failures. Shown regardless of the turn's `success` flag.
export const GeneratedAttachments = ({
  attachments,
  errors,
  className,
}: GeneratedAttachmentsProps) => {
  const { t } = useTranslation("ai-assistant");
  // Download links only ever point at http(s) — never javascript:/data:.
  const files = attachments?.filter((a) => isHttpUrl(a.url)) ?? [];

  if (!files.length && !errors?.length) return null;

  return (
    <div className={cn("flex flex-col gap-1.5", className)}>
      {files.map((a) => {
        const format = a.format?.toLowerCase() ?? "";
        const { icon: Icon, tile } = FORMAT_STYLES[format] ?? FALLBACK_STYLE;
        const meta = [
          format.toUpperCase(),
          typeof a.size_bytes === "number" ? formatBytes(a.size_bytes) : null,
          typeof a.row_count === "number"
            ? t("attachments.rows_count", { count: a.row_count })
            : null,
        ].filter(Boolean);

        return (
          <a
            key={a.url}
            href={a.url}
            download={a.name}
            target="_blank"
            rel="noopener noreferrer"
            title={t("attachments.download_file", { name: a.name })}
            className="group/file flex items-center gap-3 w-full max-w-sm rounded-lg border border-border bg-surface px-3 py-2 shadow-xs hover:border-primary/30 hover:bg-muted transition-colors"
          >
            <div
              className={cn(
                "size-9 shrink-0 rounded-md flex items-center justify-center",
                tile,
              )}
            >
              <Icon className="size-4.5" />
            </div>
            <div className="flex flex-col min-w-0 flex-1">
              <span className="text-sm font-medium text-foreground truncate">
                {a.name}
              </span>
              {meta.length > 0 && (
                <span className="text-xs text-muted-foreground">
                  {meta.join(" · ")}
                </span>
              )}
            </div>
            <Download className="size-4 shrink-0 text-muted-foreground group-hover/file:text-foreground transition-colors" />
          </a>
        );
      })}
      {errors?.map((err, i) => (
        <div
          key={i}
          className="flex items-start gap-2 w-full max-w-sm rounded-lg border border-warning/40 bg-warning-muted px-3 py-2 text-xs text-foreground"
        >
          <AlertTriangle className="size-3.5 shrink-0 mt-0.5 text-warning" />
          <span>{err}</span>
        </div>
      ))}
    </div>
  );
};
